import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, ShoppingBag, Check, ChevronLeft, ChevronRight, ChevronDown, Star } from "lucide-react";
import { ProductVisual } from "./ProductVisual";
import { Link } from "react-router-dom";
import { productPath } from "../data/seoPages";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { PRODUCTS as P_TID } from "../constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function resolvePhotoUrl(photo) {
    if (!photo) return null;
    if (photo.startsWith("http://") || photo.startsWith("https://")) return photo;
    const base = BACKEND_URL.replace(/\/$/, "");
    const path = photo.startsWith("/") ? photo : `/${photo}`;
    return `${base}${path}`;
}

export const ProductPhotos = ({ photos, name, kind }) => {
    const [index, setIndex] = useState(0);
    const urls = (photos || []).map(resolvePhotoUrl).filter(Boolean);

    if (urls.length === 0) {
        return <ProductVisual kind={kind} />;
    }

    if (urls.length === 1) {
        return (
            <img
                src={urls[0]}
                alt={name}
                className="max-h-full max-w-full object-contain rounded-lg"
            />
        );
    }

    const prev = (e) => {
        e.stopPropagation();
        setIndex((i) => (i === 0 ? urls.length - 1 : i - 1));
    };
    const next = (e) => {
        e.stopPropagation();
        setIndex((i) => (i === urls.length - 1 ? 0 : i + 1));
    };

    return (
        <div className="relative w-full h-full overflow-hidden">
            <AnimatePresence mode="wait">
                <motion.img
                    key={index}
                    src={urls[index]}
                    alt={`${name} ${index + 1}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25 }}
                    className="absolute inset-0 m-auto max-h-full max-w-full object-contain rounded-lg"
                />
            </AnimatePresence>

            <button
                type="button"
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full border border-white/50 bg-white/80 hover:bg-white shadow-sm flex items-center justify-center text-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Photo précédente"
            >
                <ChevronLeft className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full border border-white/50 bg-white/80 hover:bg-white shadow-sm flex items-center justify-center text-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Photo suivante"
            >
                <ChevronRight className="w-4 h-4" />
            </button>

            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {urls.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIndex(i);
                        }}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                            i === index ? "bg-[#0a84ff]" : "bg-neutral-300"
                        }`}
                        aria-label={`Voir la photo ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

const ProductCard = ({ product, index = 0 }) => {
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const { addItem, setOpen } = useCart();

    const inc = () => setQty((v) => Math.min(99, v + 1));
    const dec = () => setQty((v) => Math.max(1, v - 1));

    const handleAdd = () => {
        addItem(product, qty);
        setAdded(true);
        toast.success(`${qty} × ${product.name} ajouté au panier`, {
            action: {
                label: "Voir panier",
                onClick: () => setOpen(true),
            },
        });
        setTimeout(() => setAdded(false), 1400);
    };

    return (
        <motion.article
            data-testid={P_TID.card(product.id)}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: Math.min(index * 0.03, 0.25) }}
            className="group relative h-full overflow-hidden rounded-3xl border border-black/[0.06] bg-white p-5 hover:-translate-y-0.5 transition-all duration-500 hover:shadow-[0_30px_80px_-40px_rgba(10,132,255,0.35)] flex flex-col"
        >
            {/* Visual */}
            <div className="relative aspect-[16/11] rounded-2xl bg-gradient-to-br from-[#F5F5F7] to-white border border-black/[0.04] overflow-hidden">
                <div className="absolute inset-0 dp-grid-lines opacity-30" />
                <div
                    className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full blur-3xl opacity-50 pointer-events-none"
                    style={{ background: product.accent }}
                />
                <div className="relative w-full h-full p-4 group-hover:scale-105 transition-transform duration-700 flex items-center justify-center">
                    <ProductPhotos
                        photos={product.photos}
                        name={product.name}
                        kind={product.kind}
                        accent={product.accent}
                    />
                </div>
                {product.top_selling && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-mono-tech tracking-[0.12em] uppercase text-white bg-gradient-to-r from-[#0f172a] to-[#1e40af] shadow-md">
                        <Star className="w-3 h-3 fill-white" />
                        Top vente
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="pt-5 px-1 flex flex-col flex-1">
                <h3 className="font-display text-[18px] tracking-tight font-medium text-neutral-900 leading-tight">
                    <Link
                        to={productPath(product)}
                        className="hover:text-[#0a84ff] transition-colors"
                    >
                        {product.name}
                    </Link>
                </h3>
                <p className="mt-1.5 text-[13px] text-neutral-500">{product.tagline}</p>

                <ul className="mt-3.5 space-y-1.5 text-[12px] text-neutral-600 font-mono-tech">
                    {(expanded ? product.specs || [] : (product.specs || []).slice(0, 3)).map((s) => (
                        <li key={s} className="flex items-center gap-2">
                            <span
                                className="w-1 h-1 rounded-full"
                                style={{ background: product.accent }}
                            />
                            {s}
                        </li>
                    ))}
                </ul>

                {(product.specs || []).length > 3 && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-[#0a84ff] hover:text-[#0066cc] transition-colors"
                    >
                        {expanded ? "Réduire" : "Lire la suite"}
                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${
                                expanded ? "rotate-180" : ""
                            }`}
                        />
                    </button>
                )}

                <div className="mt-auto pt-5 flex items-end justify-between gap-3">
                    {/* Qty selector */}
                    <div
                        className="flex items-center gap-1 border border-black/10 rounded-full overflow-hidden"
                        data-testid={`product-qty-${product.id}`}
                    >
                        <button
                            type="button"
                            onClick={dec}
                            aria-label="Diminuer la quantité"
                            data-testid={`product-qty-minus-${product.id}`}
                            className="w-8 h-9 flex items-center justify-center hover:bg-black/[0.04] transition"
                        >
                            <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                            className="min-w-[20px] text-center text-[13px] font-medium tabular-nums"
                            data-testid={`product-qty-value-${product.id}`}
                        >
                            {qty}
                        </span>
                        <button
                            type="button"
                            onClick={inc}
                            aria-label="Augmenter la quantité"
                            data-testid={`product-qty-plus-${product.id}`}
                            className="w-8 h-9 flex items-center justify-center hover:bg-black/[0.04] transition"
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Price + Add to cart */}
                    <div className="flex flex-col items-end gap-1.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 mr-2 mb-1">
                            {product.original_price && (
                                <span className="text-[11px] text-neutral-400 line-through tabular-nums whitespace-nowrap">
                                    {product.original_price} DH / د.م.
                                </span>
                            )}
                            <span className="text-[13px] font-bold text-neutral-900 tabular-nums whitespace-nowrap">
                                {product.price_from && product.price_from !== "Sur devis"
                                    ? `${product.price_from} DH / د.م.`
                                    : "Sur devis"}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleAdd}
                            data-testid={`product-add-${product.id}`}
                            className={`w-full dp-btn text-[12.5px] py-2 px-4 justify-center transition-colors ${
                                added
                                    ? "bg-emerald-600 text-white"
                                    : "dp-btn-primary"
                            }`}
                        >
                            {added ? (
                                <>
                                    <Check className="w-3.5 h-3.5" />
                                    Ajouté
                                </>
                            ) : (
                                <>
                                    <ShoppingBag className="w-3.5 h-3.5" />
                                    Ajouter au panier
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

export default ProductCard;
